import './ListItemProductDetails.scss'

import React, { useRef, useState } from 'react'
import { Link } from 'react-router-dom'

import {
  TeachingNoteButton,
  QuickStartGuideButton,
  EducatorCopyButton,
  PlayVideoButton,
  FullVideoButton,
} from 'hb4e-ui-context'
import { CoreConstants, CoreURLs, Formatters } from 'hb4e-ui-core'
import { Banner, Icon } from 'hb4e-ui-core-ui'
import { useQuickStartGuide, useTutorial, isFutureProduct, useFullVideo } from 'hb4e-ui-product'
import { useFeatureFlags } from 'hb4e-ui-env-config'

import { getProductDetailsForListItem } from '../../../list-service'

const { IDPTabSection, KEY_NAMES } = CoreConstants
const { urlIDPProduct, urlSearch } = CoreURLs
const { hbpDateFormat, hbpMonthYearFormat, formatPrice } = Formatters

const ListItemProductDetails = ({ item }) => {
  const [shouldShowProductDetails, setShouldShowProductDetails] = useState(false)
  const { hasQuickStartGuide } = useQuickStartGuide(item.product)
  const { hasTutorial } = useTutorial(item)
  const toggleDetailsButton = useRef(null)

  // TODO: GC (11/22/22) - remove feature flag during hew-5849-missing-cta-edu-cp-for-video-explainer-tutorials clean up
  const { hew5849MissingCtaEduCpForVideoExplainerTutorials } = useFeatureFlags()

  const {
    productAvailabilityID,
    productDiscipline,
    productLanguage,
    productLength,
    productLengthLabel,
    productPrice,
    productPublicationDate,
    productRevisionDate,
    hasTeachingNote,
    hasEducatorCopy,
    hasSupplements,
  } = getProductDetailsForListItem(item)

  const { hasFullVideo } = useFullVideo(item)

  const shouldShowIncludedMaterials =
    hasTeachingNote || hasEducatorCopy || hasQuickStartGuide || hasSupplements || hasTutorial || hasFullVideo

  const includedMaterialsProductURL = urlIDPProduct(productAvailabilityID, {
    activeTab: IDPTabSection.INCLUDED_MATERIALS,
  })

  const handleOnKeyDown = (key) => {
    if (key === KEY_NAMES.ESCAPE) {
      setShouldShowProductDetails(false)
      toggleDetailsButton.current.focus()
    }
  }

  const renderWhatsIncluded = () => {
    if (!shouldShowIncludedMaterials) {
      return null
    }

    return (
      <div className="list-item-product-details__included-materials-container">
        <p className="list-item-product-details__included-materials-title">Includes:</p>
        <ul className="list-item-product-details__included-materials-list">
          {hasTutorial && hew5849MissingCtaEduCpForVideoExplainerTutorials && (
            <li className="list-item-product-details__included-materials-list-item">
              <PlayVideoButton product={item.product} />
            </li>
          )}
          {hasTeachingNote && (
            <li className="list-item-product-details__included-materials-list-item">
              <TeachingNoteButton product={item.product} theme="is-inline" />
            </li>
          )}
          {hasEducatorCopy && (
            <li className="list-item-product-details__included-materials-list-item">
              <EducatorCopyButton product={item.product} />
            </li>
          )}
          {hasQuickStartGuide && (
            <li className="list-item-product-details__included-materials-list-item">
              <QuickStartGuideButton product={item.product} />
            </li>
          )}
          {hasSupplements && (
            <li className="list-item-product-details__included-materials-list-item">
              <Link to={includedMaterialsProductURL} className="list-item-product-details__supplements-link">
                Supplements
              </Link>
            </li>
          )}
          {hasFullVideo && (
            <li className="list-item-product-details__list-item is-full-video-button">
              <FullVideoButton product={item} />
            </li>
          )}
        </ul>
      </div>
    )
  }

  const renderProductDetails = () => {
    if (!shouldShowProductDetails) {
      return null
    }

    return (
      <div
        id="list-item-product-details__additional-product-details"
        className="list-item-product-details__product-details-section animate__animated animate__fadeIn"
      >
        <div className="list-item-product-details__product-details-section-cell">
          <span className="list-item-product-details__product-details-section-field">Publication Date: </span>
          <span className="list-item-product-details__product-details-section-value">
            {hbpDateFormat(productPublicationDate)}
            <br />
            {productRevisionDate && `(Revised ${hbpMonthYearFormat(productRevisionDate)})`}
          </span>
        </div>
        <div className="list-item-product-details__product-details-section-cell">
          <span className="list-item-product-details__product-details-section-field">Product Number: </span>
          <span className="list-item-product-details__product-details-section-value">{productAvailabilityID}</span>
        </div>
        <div className="list-item-product-details__product-details-section-cell">
          <span className="list-item-product-details__product-details-section-field">Degree-granting Price: </span>
          <span className="list-item-product-details__product-details-section-value">{formatPrice(productPrice)}</span>
        </div>
        <div className="list-item-product-details__product-details-section-cell">
          <span className="list-item-product-details__product-details-section-field">Language: </span>
          <span className="list-item-product-details__product-details-section-value">{productLanguage}</span>
        </div>
        <div className="list-item-product-details__product-details-section-cell">
          <span className="list-item-product-details__product-details-section-field">Discipline:</span>
          <Link
            className="list-item-product-details__product-details-section-discipline"
            to={urlSearch({ discipline: productDiscipline })}
          >
            {productDiscipline}
          </Link>
        </div>
        <div className="list-item-product-details__product-details-section-cell">
          <span className="list-item-product-details__product-details-section-field">{productLengthLabel}: </span>
          <span className="list-item-product-details__product-details-section-value">{productLength}</span>
        </div>

        {renderWhatsIncluded()}
      </div>
    )
  }

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div className="list-item-product-details" onKeyDown={({ key }) => handleOnKeyDown(key)}>
      <button
        ref={toggleDetailsButton}
        aria-label={`${shouldShowProductDetails ? 'Hide' : 'Show'} additional product details`}
        aria-expanded={shouldShowProductDetails}
        aria-controls="list-item-product-details__additional-product-details"
        className="list-item-product-details__product-details-toggle"
        onClick={() => setShouldShowProductDetails(!shouldShowProductDetails)}
      >
        Product Details
        <span className="list-item-product-details__product-details-toggle-icon">
          {<Icon.AngleDown rotation={shouldShowProductDetails ? Icon.Effect.ROTATE_180 : 0} />}
        </span>
      </button>

      {renderProductDetails()}

      {isFutureProduct(item) && (
        <div data-testid="list-item-product-details-future-banner" className="list-item-product-details__future-banner">
          <Banner
            id="list-item-product-details-banner-left"
            className="list-item-details__banner"
            theme={Banner.Theme.BLACK_ON_SUN_DOWN_LIGHTEN_50}
            layout={Banner.Layout.WIDE}
          >
            <div className="list-item-product-details__future-banner-details">
              <div className="list-item-product-details__future-banner-details-title">Item Coming Soon</div>
              <div className="list-item-product-details__future-banner-details-desc">
                You may add this to a coursepack after it is released
              </div>
            </div>
          </Banner>
        </div>
      )}
    </div>
  )
}

export default ListItemProductDetails
